import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type RJSFForm from '@rjsf/core';
import type { FieldPathList } from '@rjsf/utils';
import { cloneDeep, has, set } from 'lodash';

export type EnqueueFieldChange = (path: FieldPathList, value: unknown) => void;

/** True if any array index segment along `path` (excluding the leaf) no longer exists in `formData`. */
export function isArrayRowGone(formData: object, path: FieldPathList): boolean {
  return path.slice(0, -1).some((segment, i) => typeof segment === 'number' && !has(formData, path.slice(0, i + 1)));
}

/**
 * Coalesces field writes that land in the same burst into a single root-level rjsf change.
 *
 * rjsf drains its `pendingChanges` queue one entry per setState callback, and React counts each of
 * those as a nested sync update (limit 50). Forms with ~130 ComputedFields whose debounce timers
 * expire together (Safari always, throttled CPUs often) would queue 50+ changes and crash with
 * "Maximum update depth exceeded". Batching them keeps the queue at one entry per burst.
 *
 * Reads rjsf internals (`pendingChanges`, `state.formData`), which is why @rjsf/* is pinned in package.json.
 */
export function useFieldChangeBatcher(rjsfRef: RefObject<RJSFForm | null>): EnqueueFieldChange {
  const pending = useRef(new Map<string, [FieldPathList, unknown]>());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(
    function flush(): void {
      flushTimer.current = null;
      const form = rjsfRef.current;
      if (!form || pending.current.size === 0) return;

      // state.formData is only current once rjsf's own queue (user edits) has drained; building on it
      // earlier would overwrite those edits when our root-level change is applied.
      if (form.pendingChanges.length > 0) {
        flushTimer.current = setTimeout(flush, 0);
        return;
      }

      const formData = cloneDeep(form.state.formData ?? {});
      pending.current.forEach(([path, value]) => {
        // Skip writes whose array row vanished since enqueue (e.g. deleted mid-burst); `set` would
        // otherwise resurrect it as a ghost row. Missing object parents are fine to create: a table
        // row whose only mapped cells are ComputedFields (a totals row) never exists in formData
        // until its first computed write lands.
        if (!isArrayRowGone(formData, path)) set(formData, path, value);
      });
      pending.current.clear();
      form.onChange(formData, []);
    },
    [rjsfRef]
  );

  useEffect(() => () => clearTimeout(flushTimer.current ?? undefined), []);

  return useCallback(
    (path, value) => {
      pending.current.set(path.join('.'), [path, value]);
      // A macrotask (not a microtask) so every timer already expired in the same burst is collected.
      if (flushTimer.current == null) flushTimer.current = setTimeout(flush, 0);
    },
    [flush]
  );
}
